<template>
  <v-dialog :value="isOpen" width="700px" @input="$emit('close')">
    <v-card>
      <v-card-title class="error white--text headline">
        <Icon
          icon="mdi-alert-circle"
          :small="false"
          iconColor="white"
          large
        />
        {{ $t('collisionDetected') }}
        <v-spacer />
        <Icon
          icon="mdi-close"
          :small="false"
          :large="true"
          iconColor="white"
          @icon-clicked="$emit('close')"
        />
      </v-card-title>
      <v-card-text class="py-5" style="max-height: 500px; overflow-y: auto;">
        <v-container fluid>
          <div v-if="overlapData && overlapData.length > 0">
            <v-expansion-panels popout v-model="openPanel">
              <v-expansion-panel v-for="(destination, index) in overlapData" :key="index">
                <v-expansion-panel-header>
                  {{ destinationAccountName(destination.destinationAccountId) }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12">
                      <div class="mt-2 mb-2">{{ $t('ticketBooks') }}:</div>
                      <v-card v-for="(book, idx) in destination.ticketBooks" :key="idx" class="mb-4" outlined>
                        <v-card-text>
                          <div class="d-flex justify-space-between align-start">
                            <div style="flex: 1;">
                              <div class="d-flex align-center mb-2">
                                <Icon
                                small
                                class="mr-2"
                                icon="mdi-book-open-variant"
                                :tooltipText="$t('ticketBook')" />
                                <span class="text-sm">{{ getTicketBookById(book.ticketBookId).name }}</span>
                              </div>
                              <div class="d-flex align-center mb-2">
                                <Icon
                                small
                                class="mr-2"
                                icon="mdi-account"
                                :tooltipText="$t('account')" />
                                <span class="text-sm">{{ accountName(getTicketBookById(book.ticketBookId).accountId) }}</span>
                              </div>
                              <div class="d-flex align-center">
                                <Icon
                                small
                                class="mr-2"
                                icon="mdi-crosshairs-gps"
                                :tooltipText="$t('tract')"
                                @icon-clicked="openTract(getTicketBookById(book.ticketBookId).tractId)"/>
                                <span class="text-sm">{{ getTicketBookById(book.ticketBookId).tract }}</span>
                              </div>
                            </div>
                            <div class="ml-3">
                              <div class="text-xs font-weight-bold mb-1">{{ $t('overlappingPageNumbers') }}</div>
                              <v-tooltip bottom color="primary" :disabled="!isTextTruncated(formatPageNumbers(book.overlappingUnusedPageNumbers), 25)">
                                <template #activator="{ on, attrs }">
                                  <div
                                    class="text-xs"
                                    style="word-break: break-word; max-width: 190px;"
                                    v-bind="attrs"
                                    v-on="on"
                                  >
                                    {{ truncatePageNumbers(formatPageNumbers(book.overlappingUnusedPageNumbers), 25) }}
                                  </div>
                                </template>
                                <span class="subtitle-1 white--text">{{ formatPageNumbers(book.overlappingUnusedPageNumbers) }}</span>
                              </v-tooltip>
                            </div>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import RouterJump from '@/model/RouterJump'
import { UserClaims } from '@/utils/Enumerations.js'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'

export default {
  name: 'TicketBookOverlapDialog',

  components: {
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    overlapData: {
      type: Array,
      default: () => []
    },
    ticketBooks: {
      type: Array,
      default: () => []
    }
  },

  data: () => ({
    openPanel: undefined
  }),

  watch: {
    overlapData: {
      deep: true,
      immediate: true,
      handler () {
        if (this.overlapData.length !== 1) return
        this.$nextTick(_ => { this.openPanel = 0 })
      }
    }
  },

  computed: {
    ...mapGetters('account', ['allAccounts']),
    ...mapGetters('user', ['companyInfo'])
  },

  methods: {
    ...mapMutations('global', ['setRouterJump']),
    destinationAccountName (accountId) {
      return this.accountName(accountId)
    },

    accountName (accountId) {
      const account = this.allAccounts.find(a => a.accountId === accountId)
      return account ? account.name : `Account ${accountId}`
    },

    getTicketBookById (bookId) {
      return this.ticketBooks.find(b => b.ticketBookId === bookId)
    },

    formatPageNumbers (pageNumbers) {
      if (!pageNumbers || pageNumbers.length === 0) {
        return this.$t('noPagesOverlapping')
      }

      const ranges = []
      let start = pageNumbers[0]
      let end = pageNumbers[0]

      for (let i = 1; i < pageNumbers.length; i++) {
        if (pageNumbers[i] === end + 1) {
          end = pageNumbers[i]
        } else {
          ranges.push(start === end ? `${start}` : `${start}-${end}`)
          start = pageNumbers[i]
          end = pageNumbers[i]
        }
      }
      ranges.push(start === end ? `${start}` : `${start}-${end}`)

      return ranges.join(', ')
    },

    truncatePageNumbers (text, limit) {
      if (!text || text.length <= limit) {
        return text
      }
      return text.substring(0, limit) + '...'
    },

    isTextTruncated (text, limit) {
      return text && text.length > limit
    },

    openTract (tractId) {
      if (!userAssignedClaim(UserClaims.ContractManager)) {
        this.setSnackError(this.$t('userDoesNotHavePermissionToEditTract'))
        return
      }
      const sourceObject = { tractId: tractId }
      const options = { readonly: false }
      this.setRouterJump(new RouterJump('UpdateExpirationDate', 'Tracts', sourceObject, options))
      this.$router.push('tracts')
      this.$emit('close')
    }
  }
}
</script>
